import { useGT } from 'gt-react';
import type { InventoryPolicyOverrideRow } from 'src/api/rpc/hooks/procurement/useInventoryPolicyOverrides';
import {
  OverrideHistoryList,
  OverrideHistoryRow,
  OverrideRowActions,
  OverrideStatusBadges,
} from '../components/OverrideHistory';
import {
  overrideDeactivationLine,
  overrideMetaLine,
  replacedOverrideIds,
} from '../components/overrideRows';
import {
  describeOverride,
  isPolicyOverrideActionable,
  overrideStatuses,
  policyHeadlineClasses,
  policyStatusBadgeClasses,
  policyStatusLabel,
  type PolicyOverrideStatus,
} from './policyOverrideRows';

// Every policy override ever set for one warehouse x product, newest first.
// The row layout is shared with the lead-time list; what belongs to the policy
// is the set of fields as the headline and the shadowed-vs-in-effect status,
// which is per field rather than per row (see `overrideStatuses`).

interface PolicyHistoryRow {
  override: InventoryPolicyOverrideRow;
  status: PolicyOverrideStatus;
  /** A newer row supersedes this one (an edit), as opposed to a plain revoke. */
  replaced: boolean;
}

function PolicyRow({
  row: { override: o, status, replaced },
  onEdit,
  onRequestRevoke,
}: {
  row: PolicyHistoryRow;
  onEdit: (o: InventoryPolicyOverrideRow) => void;
  onRequestRevoke: (o: InventoryPolicyOverrideRow, scheduled: boolean) => void;
}) {
  const gt = useGT();
  return (
    <OverrideHistoryRow
      headline={
        <span className={policyHeadlineClasses(status)}>
          {describeOverride(o, gt)}
        </span>
      }
      status={
        <OverrideStatusBadges
          label={policyStatusLabel(status, gt)}
          className={policyStatusBadgeClasses(status)}
          filled={status !== 'inactive'}
          needsReview={o.needsReview}
        />
      }
      meta={overrideMetaLine(o, gt)}
      deactivation={overrideDeactivationLine(o, replaced, gt)}
      actions={
        isPolicyOverrideActionable(status) ? (
          <OverrideRowActions
            override={o}
            scheduled={status === 'scheduled'}
            onEdit={onEdit}
            onRequestRevoke={onRequestRevoke}
          />
        ) : null
      }
    />
  );
}

export function PolicyOverrideHistory({
  overrides,
  onEdit,
  onRequestRevoke,
}: {
  /** Newest first, as the list endpoint returns them. */
  overrides: readonly InventoryPolicyOverrideRow[];
  onEdit: (o: InventoryPolicyOverrideRow) => void;
  onRequestRevoke: (o: InventoryPolicyOverrideRow, scheduled: boolean) => void;
}) {
  const statuses = overrideStatuses(overrides);
  const replacedIds = replacedOverrideIds(overrides);
  const rows: PolicyHistoryRow[] = overrides.map((o) => ({
    override: o,
    status: statuses.get(o.id) ?? 'inactive',
    replaced: replacedIds.has(o.id),
  }));
  if (rows.length === 0) return null;
  return (
    <OverrideHistoryList>
      {rows.map((row) => (
        <PolicyRow
          key={row.override.id}
          row={row}
          onEdit={onEdit}
          onRequestRevoke={onRequestRevoke}
        />
      ))}
    </OverrideHistoryList>
  );
}
